import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ImageIcon, Upload, CheckCircle2, AlertCircle, Coins } from 'lucide-react';
import api, { UPLOAD_TIMEOUT } from '../lib/api';
import { supabase } from '../lib/supabase';
import { compressImage } from '../lib/imageCompression';
import { usePricing } from '../hooks/usePricing';
import { Input } from './ui/input';
import { BannerPreview } from './BannerCreative';
import { formatBannerDetail } from '../lib/bannerDetail';

const STEPS = ['job', 'image', 'duration', 'confirm'];
const MAX_RAW_BYTES = 20 * 1024 * 1024; // 20MB pre-compression (mirrors ImageUpload.js)
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const DURATION_TIERS = [
  { days: 3, priceKey: 'banner_price_3d' },
  { days: 7, priceKey: 'banner_price_7d' },
  { days: 14, priceKey: 'banner_price_14d' },
  { days: 30, priceKey: 'banner_price_30d' },
];

/**
 * Employer self-serve "buy a homepage banner" wizard (spec §8). Select one of the
 * employer's own OPEN jobs (prefilled if `jobId` is passed), upload + preview an
 * image in the carousel's 4:1 slot aspect, pick a duration tier, confirm, and
 * POST /api/banners as multipart with a generated idempotency key.
 *
 * Mount/unmount controlled by the parent (no `open` prop) — call `onClose()` to
 * dismiss, whether from the close button, ESC, or a successful purchase.
 */
const BannerCreateModal = ({ jobId, onClose }) => {
  const { t } = useTranslation();
  const { pricing } = usePricing();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState('job');
  const [myJobs, setMyJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [activeBannerJobIds, setActiveBannerJobIds] = useState(new Set());
  const [selectedJobId, setSelectedJobId] = useState(jobId || '');

  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [imagePosition, setImagePosition] = useState(50);
  const [compressing, setCompressing] = useState(false);
  const [headline, setHeadline] = useState('');
  const [headlineTouched, setHeadlineTouched] = useState(false);
  const [businessName, setBusinessName] = useState('');

  const [durationDays, setDurationDays] = useState(7);
  const [balance, setBalance] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successData, setSuccessData] = useState(null);

  // One idempotency key per modal mount — a network retry reuses it (no double charge).
  const idempotencyKey = useMemo(
    () => `banner-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    []
  );

  const handleClose = () => {
    if (onClose) onClose();
  };

  // Load the employer's own OPEN jobs (banners can only link to `jobs`, not gigs).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingJobs(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        if (!userId) { if (!cancelled) setLoadingJobs(false); return; }
        const { data, error } = await supabase
          .from('jobs')
          .select('id, title, slug, status, location, locations, salary_min, salary_max, salary_period')
          .eq('employer_id', userId)
          .eq('status', 'open')
          .order('created_at', { ascending: false });
        if (error) throw error;
        if (!cancelled) setMyJobs(data || []);

        // Business name for the live preview's label — same field the real carousel
        // shows (preview parity, spec §6).
        const { data: prof } = await supabase
          .from('profiles')
          .select('business_name')
          .eq('id', userId)
          .limit(1);
        if (!cancelled && prof && prof[0]) setBusinessName(prof[0].business_name || '');
      } catch (err) {
        console.error('[BannerCreateModal] Failed to fetch employer jobs:', err);
        if (!cancelled) setMyJobs([]);
      } finally {
        if (!cancelled) setLoadingJobs(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load the employer's existing ACTIVE banners so the job picker below can exclude
  // jobs that already have one running — picking one would 409 (one-active-banner-
  // per-job backstop in purchase_banner).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const res = await api.get('/api/banners/mine', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        const mine = res.data?.banners || [];
        const activeIds = new Set(mine.filter((b) => b.status === 'active').map((b) => b.job_id));
        if (!cancelled) setActiveBannerJobIds(activeIds);
      } catch (err) {
        console.error('[BannerCreateModal] Failed to fetch existing banners:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load current credit balance (for the duration/price step).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const res = await api.get('/api/credits/balance', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!cancelled) setBalance(res.data?.balance ?? 0);
      } catch (err) {
        console.error('[BannerCreateModal] Failed to fetch credit balance:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Exclude jobs that already have an active banner — picking one would 409 against
  // the one-active-banner-per-job backstop in purchase_banner.
  const eligibleJobs = useMemo(
    () => myJobs.filter((j) => !activeBannerJobIds.has(j.id)),
    [myJobs, activeBannerJobIds]
  );
  const selectedJob = myJobs.find((j) => j.id === selectedJobId);
  const selectedPrice = pricing[DURATION_TIERS.find((tier) => tier.days === durationDays)?.priceKey] ?? 0;
  const insufficientCredits = typeof balance === 'number' && balance < selectedPrice;

  // Prefill the headline from the selected job's title until the employer edits it.
  // Keyed on the DERIVED selectedJob (not the Select's onValueChange) so the prefill
  // also fires for the jobId-prop call sites (JobDetails/PostJob) once myJobs resolves.
  useEffect(() => {
    if (selectedJob && !headlineTouched) {
      setHeadline(selectedJob.title || '');
    }
  }, [selectedJob, headlineTouched]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileName = file.name?.toLowerCase() || '';
    const hasValidExtension = /\.(jpe?g|png|webp)$/i.test(fileName);
    if (!ALLOWED_TYPES.includes(file.type) && !(file.type === '' && hasValidExtension)) {
      toast.error(t('banner.errorImageType', 'Only JPEG, PNG, or WebP images are allowed.'));
      return;
    }
    if (file.size > MAX_RAW_BYTES) {
      toast.error(t('banner.errorImageSize', 'Image must be smaller than 20MB.'));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => setImagePreviewUrl(reader.result);
    reader.readAsDataURL(file);

    setCompressing(true);
    try {
      const compressed = await compressImage(file, 'jobImage');
      setImageFile(compressed);
    } catch {
      setImageFile(file);
    } finally {
      setCompressing(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedJobId || !imageFile || !durationDays || submitting) return;
    setSubmitting(true);
    setErrorMessage('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error(t('common.loginRequired', 'Please log in.'));
        return;
      }
      const form = new FormData();
      form.append('job_id', selectedJobId);
      form.append('duration_days', String(durationDays));
      form.append('idempotency_key', idempotencyKey);
      form.append('headline', headline.trim());
      form.append('image_position', String(imagePosition));
      form.append('file', imageFile, imageFile.name || 'banner.jpg');

      const res = await api.post('/api/banners', form, {
        headers: { Authorization: `Bearer ${session.access_token}` },
        timeout: UPLOAD_TIMEOUT,
      });
      setSuccessData(res.data || {});
      toast.success(t('banner.createSuccess', 'Your banner is live on the homepage!'));
    } catch (err) {
      const status = err?.response?.status;
      const detail = typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : '';
      if (status === 402) {
        setErrorMessage(detail || t('banner.errorInsufficientCredits', 'Not enough credits for this banner.'));
      } else if (status === 409) {
        setErrorMessage(detail || t('banner.errorSlotsFull', 'All banner slots are full right now. Please try again later.'));
      } else if (status === 403) {
        setErrorMessage(detail || t('banner.errorVerifyRequired', 'Verify your business to buy a homepage banner.'));
      } else {
        setErrorMessage(detail || t('banner.errorGeneric', 'Failed to create the banner. Please try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const stepIndex = STEPS.indexOf(step);
  const goNext = () => setStep(STEPS[Math.min(stepIndex + 1, STEPS.length - 1)]);
  const goBack = () => setStep(STEPS[Math.max(stepIndex - 1, 0)]);

  const canProceedFromJob = !!selectedJobId && eligibleJobs.some((j) => j.id === selectedJobId);
  const canProceedFromImage = !!imageFile && !compressing;
  const canProceedFromDuration = !!durationDays;

  // ---- Success state ----
  if (successData) {
    return (
      <Dialog open onOpenChange={(next) => { if (!next) handleClose(); }}>
        <DialogContent className="sm:max-w-md max-h-[90vh] max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle2 size={22} />
              {t('banner.successTitle', 'Banner created!')}
            </DialogTitle>
            <DialogDescription>
              {t('banner.successDesc', 'Your banner is now rotating on the homepage carousel.')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-between">
            <Link to="/dashboard" onClick={handleClose} className="text-sm font-medium text-primary underline underline-offset-2">
              {t('banner.viewMyBanners', 'View My Banners')}
            </Link>
            <Button onClick={handleClose}>{t('common.done', 'Done')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(next) => { if (!next) handleClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('banner.modalTitle', 'Put your job on the homepage banner')}</DialogTitle>
          <DialogDescription>
            {t('banner.modalSubtitle', 'Get more visibility — your banner rotates at the top of the jobs page.')}
          </DialogDescription>
        </DialogHeader>

        {/* Step progress dots */}
        <div className="flex items-center gap-1.5">
          {STEPS.map((s, i) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= stepIndex ? 'bg-primary' : 'bg-muted'}`} />
          ))}
        </div>

        {/* Step 1: select job */}
        {step === 'job' && (
          <div className="space-y-3">
            <Label>{t('banner.selectJob', 'Which open job should this banner link to?')}</Label>
            {loadingJobs ? (
              <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading...')}</p>
            ) : myJobs.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('banner.noOpenJobs', 'You have no open job postings yet. Post a job first, then come back to buy a banner.')}
              </p>
            ) : eligibleJobs.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('banner.allJobsHaveBanners', 'All of your open jobs already have an active banner.')}
              </p>
            ) : (
              <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                <SelectTrigger data-testid="banner-job-select">
                  <SelectValue placeholder={t('banner.selectJobPlaceholder', 'Select a job')} />
                </SelectTrigger>
                <SelectContent>
                  {eligibleJobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}

        {/* Step 2: design the banner — headline + image + focal, with a TRUE live
            preview rendered by the same BannerCreative the real carousel uses. Two
            crops are shown (desktop ≈4.4:1, mobile ≈2.2:1) because the live carousel
            is h-[160px] mobile / h-[280px] desktop at content width — a single fixed
            4:1 box was exactly the v1 "preview ≠ reality" bug (spec §6). */}
        {step === 'image' && (
          <div className="space-y-3">
            <Label htmlFor="banner-headline">{t('banner.headlineLabel', 'Headline')}</Label>
            <Input
              id="banner-headline"
              value={headline}
              maxLength={80}
              onChange={(e) => { setHeadline(e.target.value); setHeadlineTouched(true); }}
              data-testid="banner-headline-input"
            />

            <Label>{t('banner.uploadImage', 'Upload a banner image')}</Label>
            <p className="text-xs text-muted-foreground">
              {t('banner.imageRecommendation', 'Recommended size 1200×300 (4:1). JPEG, PNG, or WebP.')}
            </p>

            {!imagePreviewUrl && (
              <div className="relative w-full aspect-[4/1] rounded-lg overflow-hidden border-2 border-border bg-muted">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex h-full w-full flex-col items-center justify-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ImageIcon size={28} />
                  <span className="text-xs">{t('banner.clickToUpload', 'Click to upload an image')}</span>
                </button>
              </div>
            )}

            {imagePreviewUrl && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">
                  {t('banner.livePreviewLabel', 'Live preview — exactly what publishes')}
                </p>
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">{t('banner.previewDesktop', 'Desktop')}</p>
                  <BannerPreview
                    mode="desktop"
                    imageUrl={imagePreviewUrl}
                    imagePosition={imagePosition}
                    headline={headline || selectedJob?.title || ''}
                    detail={formatBannerDetail(selectedJob)}
                    label={businessName}
                    tag="sponsored"
                    cta="apply"
                    showReport={false}
                  />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">{t('banner.previewMobile', 'Mobile')}</p>
                  <BannerPreview
                    mode="mobile"
                    className="w-3/5"
                    imageUrl={imagePreviewUrl}
                    imagePosition={imagePosition}
                    headline={headline || selectedJob?.title || ''}
                    detail={formatBannerDetail(selectedJob)}
                    label={businessName}
                    tag="sponsored"
                    cta="apply"
                    showReport={false}
                  />
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />

            <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={compressing} className="w-full">
              <Upload className="mr-2 h-4 w-4" />
              {compressing ? t('banner.compressing', 'Processing image...') : imagePreviewUrl ? t('banner.changeImage', 'Change image') : t('banner.chooseImage', 'Choose image')}
            </Button>

            {imagePreviewUrl && (
              <div className="space-y-1">
                <Label className="text-xs">{t('banner.adjustFocus', 'Adjust focal point')}</Label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={imagePosition}
                  onChange={(e) => setImagePosition(parseInt(e.target.value, 10))}
                  className="w-full h-2 bg-gray-200 rounded-lg cursor-pointer accent-primary"
                />
              </div>
            )}
          </div>
        )}

        {/* Step 3: duration + price */}
        {step === 'duration' && (
          <div className="space-y-3">
            <Label>{t('banner.selectDuration', 'How long should it run?')}</Label>
            <RadioGroup value={String(durationDays)} onValueChange={(v) => setDurationDays(parseInt(v, 10))}>
              {DURATION_TIERS.map((tier) => (
                <label
                  key={tier.days}
                  htmlFor={`banner-duration-${tier.days}`}
                  className={`flex items-center justify-between rounded-lg border p-3 cursor-pointer ${durationDays === tier.days ? 'border-primary bg-primary/5' : 'border-border'}`}
                >
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <RadioGroupItem value={String(tier.days)} id={`banner-duration-${tier.days}`} />
                    {t('banner.durationDays', { count: tier.days, defaultValue: '{{count}} days' })}
                  </span>
                  <span className="text-sm font-semibold">
                    {t('banner.creditsCost', { credits: pricing[tier.priceKey], defaultValue: '{{credits}} credits' })}
                  </span>
                </label>
              ))}
            </RadioGroup>
            {typeof balance === 'number' && (
              <div className={`rounded-lg border p-3 text-xs ${insufficientCredits ? 'border-destructive text-destructive' : 'border-border text-muted-foreground'}`}>
                {insufficientCredits
                  ? t('banner.insufficientCreditsDetail', { balance, price: selectedPrice, defaultValue: 'Not enough credits — you have {{balance}}, this costs {{price}}.' })
                  : t('banner.balance', { balance, defaultValue: 'Balance: {{balance}} credits' })}
                {insufficientCredits && (
                  <Link to="/credits" className="ml-1 underline font-medium">{t('banner.topUp', 'Top up credits')}</Link>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 4: confirm */}
        {step === 'confirm' && (
          <div className="space-y-3">
            <div className="rounded-lg border p-3 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t('banner.confirmJob', 'Job')}</span>
                <span className="font-medium truncate max-w-[60%] text-right">{selectedJob?.title}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t('banner.confirmDuration', 'Duration')}</span>
                <span className="font-medium">{t('banner.durationDays', { count: durationDays, defaultValue: '{{count}} days' })}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1 text-muted-foreground"><Coins size={13} /> {t('banner.confirmCost', 'Cost')}</span>
                <span className="font-semibold">{t('banner.creditsCost', { credits: selectedPrice, defaultValue: '{{credits}} credits' })}</span>
              </div>
              {imagePreviewUrl && (
                <BannerPreview
                  mode="desktop"
                  imageUrl={imagePreviewUrl}
                  imagePosition={imagePosition}
                  headline={headline || selectedJob?.title || ''}
                  detail={formatBannerDetail(selectedJob)}
                  label={businessName}
                  tag="sponsored"
                  cta="apply"
                  showReport={false}
                />
              )}
            </div>
            {errorMessage && (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          {stepIndex > 0 ? (
            <Button type="button" variant="outline" onClick={goBack} disabled={submitting}>
              {t('common.back', 'Back')}
            </Button>
          ) : (
            <Button type="button" variant="outline" onClick={handleClose}>
              {t('common.cancel', 'Cancel')}
            </Button>
          )}

          {step === 'job' && (
            <Button type="button" onClick={goNext} disabled={!canProceedFromJob}>
              {t('common.next', 'Next')}
            </Button>
          )}
          {step === 'image' && (
            <Button type="button" onClick={goNext} disabled={!canProceedFromImage}>
              {t('common.next', 'Next')}
            </Button>
          )}
          {step === 'duration' && (
            <Button type="button" onClick={goNext} disabled={!canProceedFromDuration}>
              {t('common.next', 'Next')}
            </Button>
          )}
          {step === 'confirm' && (
            <Button type="button" onClick={handleSubmit} disabled={submitting || insufficientCredits}>
              {submitting ? t('banner.submitting', 'Creating...') : t('banner.confirmAndPay', { credits: selectedPrice, defaultValue: 'Confirm ({{credits}} credits)' })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BannerCreateModal;
